import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  getFormTemplates,
  getFormTemplate,
  createFormTemplate,
  updateFormTemplate
} from 'services/taskService'
import { getFileTypesRoutine } from 'ducks/files/actions'
import { getFileTypes } from 'ducks/files/selectors'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import { FORM_CATEGORIES, getCategoryLabel } from '../formCategories'
import FormRenderer from './FormRenderer'
import FormBuilder from './FormBuilder'
import FormTemplateSharingPanel from './FormTemplateSharingPanel'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import RecommendIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { getRecommendedObligationForms } from 'services/checklistService'

const shareSummaryText = t => {
  const parts = []
  if (t.visibility === 'private') parts.push('Only the owner')
  else if (t.visibility === 'organisation') parts.push('Whole organisation')
  else if (t.visibility === 'shared') {
    parts.push(t.sharedWithCount ? `${t.sharedWithCount} ${t.sharedWithCount === 1 ? 'person' : 'people'}` : 'specific people')
  }
  if (t.buildingScope === 'selected') parts.push('selected buildings')
  return parts.length ? parts.join(' · ') : null
}

const PAGE_SIZE = 20

const SORT_OPTIONS = [
  { value: 'name', label: 'Name (A to Z)' },
  { value: '-name', label: 'Name (Z to A)' },
  { value: '-updated_at', label: 'Recently updated' },
  { value: '-created_at', label: 'Newest first' }
]

const accessBadge = template => {
  if (template.isGlobal) {
    return { label: 'System form', Icon: PublicOutlinedIcon, color: '#33becc', bg: '#e6f7f9' }
  }
  if (template.accessSource === 'owned') {
    return { label: 'Your form', Icon: PersonOutlineOutlinedIcon, color: '#15803d', bg: '#dcfce7' }
  }
  if (template.accessSource === 'shared') {
    return { label: 'Shared with you', Icon: GroupsOutlinedIcon, color: '#c2410c', bg: '#ffedd5' }
  }
  if (template.accessSource === 'organisation') {
    return { label: 'From your organisation', Icon: GroupsOutlinedIcon, color: '#4b5563', bg: '#f1f1f4' }
  }
  return { label: 'Shared with you', Icon: GroupsOutlinedIcon, color: '#4b5563', bg: '#f1f1f4' }
}

const FormTemplatePickerModal = ({
  open,
  onClose,
  value,
  onSelect,
  buildingPassportId,
  allowNone = false,
  noneLabel = 'No form needed',
  noneDescription = 'The person doing the work will not have to fill anything in.',
  title = 'Choose a form',
  description = 'Pick the form the person doing this work will fill in. You can preview a form before you choose it.',
  initialPreviewTemplate = null,
  programmeObligationId = null
}) => {
  const dispatch = useDispatch()
  const fileTypes = useSelector(getFileTypes) || []

  const [mode, setMode] = useState('list')
  const [activeTemplate, setActiveTemplate] = useState(null)
  const [activeTemplateLoading, setActiveTemplateLoading] = useState(false)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const [sort, setSort] = useState('name')
  const [showSystem, setShowSystem] = useState(true)

  const [templates, setTemplates] = useState([])
  const [meta, setMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [currentTemplate, setCurrentTemplate] = useState(null)

  useEffect(() => {
    if (!open || !value) { setCurrentTemplate(null); return }
    let cancelled = false
    getFormTemplate(value)
      .then(res => { if (!cancelled) setCurrentTemplate(res?.data?.data || res?.data || null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [open, value])

  const requestIdRef = useRef(0)

  const [recommendations, setRecommendations] = useState([])
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)

  useEffect(() => {
    if (!open || !programmeObligationId || !buildingPassportId) {
      setRecommendations([])
      return undefined
    }
    let cancelled = false
    setRecommendationsLoading(true)
    getRecommendedObligationForms(buildingPassportId, programmeObligationId)
      .then(res => {
        if (!cancelled) setRecommendations((res?.data?.data || []).filter(item => item?.template?.id))
      })
      .catch(() => { if (!cancelled) setRecommendations([]) })
      .finally(() => { if (!cancelled) setRecommendationsLoading(false) })
    return () => { cancelled = true }
  }, [open, programmeObligationId, buildingPassportId])

  useEffect(() => {
    if (!open) return
    if (!fileTypes.length) dispatch(getFileTypesRoutine())
  }, [open, dispatch, fileTypes.length])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchPage = useCallback(async page => {
    const requestId = ++requestIdRef.current
    if (page > 1) setLoadingMore(true)
    else setLoading(true)
    setError('')

    try {
      const response = await getFormTemplates({
        page,
        limit: PAGE_SIZE,
        sort,
        search: debouncedSearch || undefined,
        category: category || undefined,
        scope: showSystem ? undefined : 'hide_system',
        buildingPassportId: buildingPassportId || undefined
      })

      if (requestId !== requestIdRef.current) return

      const incoming = (response?.data?.data || []).filter(item => !item.hiddenFromLibrary)
      const responseMeta = response?.data?.meta || {}
      const currentPage = responseMeta.currentPage ?? page

      setTemplates(previous =>
        currentPage > 1
          ? [...previous, ...incoming.filter(item => !previous.some(existing => existing.id === item.id))]
          : incoming
      )
      setMeta({
        currentPage,
        lastPage: responseMeta.lastPage ?? currentPage,
        total: responseMeta.total ?? incoming.length
      })
    } catch (requestError) {
      if (requestId !== requestIdRef.current) return
      setError('We could not load your forms. Please check your connection and try again.')
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [sort, debouncedSearch, category, showSystem, buildingPassportId])

  useEffect(() => {
    if (!open || mode !== 'list') return
    fetchPage(1)
  }, [open, mode, fetchPage])

  const backToList = useCallback(() => {
    setMode('list')
    setActiveTemplate(null)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = event => {
      if (event.key !== 'Escape') return
      if (mode === 'list') onClose?.()
      else backToList()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, mode, onClose, backToList])

  useEffect(() => {
    if (open) return
    setMode('list')
    setActiveTemplate(null)
    setSearch('')
    setDebouncedSearch('')
    setCategory('')
    setError('')
  }, [open])

  const openTemplate = async (template, nextMode) => {
    setMode(nextMode)
    setActiveTemplate(template)
    setActiveTemplateLoading(true)
    try {
      const response = await getFormTemplate(template.id)
      setActiveTemplate(response?.data?.data || response?.data || template)
    } catch (requestError) {
      setActiveTemplate(template)
    } finally {
      setActiveTemplateLoading(false)
    }
  }

  const initialPreviewId = initialPreviewTemplate?.id || null
  useEffect(() => {
    if (open && initialPreviewId) {
      openTemplate(initialPreviewTemplate, 'preview')
    }
  }, [open, initialPreviewId, initialPreviewTemplate])

  const handleUse = template => {
    onSelect?.(template)
    onClose?.()
  }

  const handleSaveTemplate = async data => {
    const { autoPopulateMap, fileTypeId, ...rest } = data
    const payload = {
      ...rest,
      auto_populate_map: autoPopulateMap || null,
      file_type_id: fileTypeId || null
    }
    setSaving(true)
    setError('')
    try {
      const response = mode === 'create'
        ? await createFormTemplate(payload)
        : await updateFormTemplate({ id: activeTemplate.id, ...payload })
      const saved = response?.data?.data || response?.data
      setActiveTemplate(saved)
      setMode('edit')
      setTemplates(previous => {
        const exists = previous.some(item => item.id === saved.id)
        return exists
          ? previous.map(item => (item.id === saved.id ? { ...item, ...saved } : item))
          : [saved, ...previous]
      })
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
        'We could not save this form. Please check the questions and try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleSharingSaved = updated => {
    if (!updated?.id) return
    setActiveTemplate(previous => ({ ...previous, ...updated }))
    setTemplates(previous =>
      previous.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
    )
  }

  const categories = useMemo(
    () => FORM_CATEGORIES.filter(option => option.value),
    []
  )

  const displayTemplates = useMemo(() => {
    if (!value || debouncedSearch) return templates
    const pinned = (currentTemplate && currentTemplate.id === value)
      ? currentTemplate
      : templates.find(item => item.id === value)
    if (!pinned) return templates
    return [pinned, ...templates.filter(item => item.id !== pinned.id)]
  }, [value, debouncedSearch, currentTemplate, templates])

  const showRecommendations = !debouncedSearch && !category &&
    (recommendationsLoading || recommendations.length > 0)
  const recommendedIds = showRecommendations
    ? new Set(recommendations.map(item => item.template.id))
    : new Set()
  const listTemplates = displayTemplates.filter(template => !recommendedIds.has(template.id))

  if (!open) return null

  const hasMore = meta.currentPage < meta.lastPage

  const renderRow = (template, recommendation = null) => {
    const badge = accessBadge(template)
    const BadgeIcon = badge.Icon
    const isSelected = value === template.id
    return (
      <TemplateRow
        key={`${recommendation ? 'rec' : 'row'}-${template.id}`}
        $selected={isSelected}
        onClick={() => handleUse(template)}
        role='button'
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') handleUse(template)
        }}
      >
        <RowMain>
          <RowTitleLine>
            <RowTitle>{template.name}</RowTitle>
            {recommendation && (
              <RowBadge $color='#1f7f89' $bg='#e6f7f9'>
                <RecommendIcon style={{ fontSize: 14 }} />
                Recommended
              </RowBadge>
            )}
            <RowBadge $color={badge.color} $bg={badge.bg}>
              <BadgeIcon style={{ fontSize: 14 }} />
              {badge.label}
            </RowBadge>
            {template.isLocked && (
              <RowBadge $color='#c2410c' $bg='#ffedd5' title='Only the owner can change this form'>
                <LockOutlinedIcon style={{ fontSize: 14 }} />
                Locked
              </RowBadge>
            )}
            {isSelected && (
              <SelectedFlag>
                <CheckCircleIcon style={{ fontSize: 16 }} /> Currently chosen
              </SelectedFlag>
            )}
          </RowTitleLine>
          {recommendation && (
            <RecommendReason>{recommendation.reason}</RecommendReason>
          )}
          {template.description && (
            <RowDescription>{template.description}</RowDescription>
          )}
          <RowMeta>
            {getCategoryLabel(template.category) && (
              <MetaItem>{getCategoryLabel(template.category)}</MetaItem>
            )}
            <MetaItem>
              {template.fieldCount || 0} question{template.fieldCount === 1 ? '' : 's'}
            </MetaItem>
            {template.creator && (
              <MetaItem>
                Created by {[template.creator.firstName, template.creator.lastName]
                  .filter(Boolean).join(' ') || 'a colleague'}
              </MetaItem>
            )}
            {template.canShare && shareSummaryText(template) && (
              <MetaItem>Shared: {shareSummaryText(template)}</MetaItem>
            )}
          </RowMeta>
        </RowMain>
        <RowActions>
          <GhostButton
            type='button'
            onClick={event => { event.stopPropagation(); openTemplate(template, 'preview') }}
          >
            <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
            Preview
          </GhostButton>
          {template.canEdit && (
            <GhostButton
              type='button'
              onClick={event => { event.stopPropagation(); openTemplate(template, 'edit') }}
            >
              <EditOutlinedIcon style={{ fontSize: 18 }} />
              Edit
            </GhostButton>
          )}
          {template.canShare && !template.canEdit && (
            <GhostButton
              type='button'
              onClick={event => { event.stopPropagation(); openTemplate(template, 'edit') }}
            >
              <ShareOutlinedIcon style={{ fontSize: 18 }} />
              Share
            </GhostButton>
          )}
          <UseButton
            type='button'
            onClick={event => { event.stopPropagation(); handleUse(template) }}
          >
            Use this form
          </UseButton>
        </RowActions>
      </TemplateRow>
    )
  }

  const body = (
    <Overlay onClick={onClose} role='presentation'>
      <Dialog onClick={event => event.stopPropagation()} role='dialog' aria-modal='true' aria-label={title}>
        {mode === 'list' && (
          <>
            <Header>
              <HeaderText>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
              </HeaderText>
              <CloseButton type='button' onClick={onClose} aria-label='Close without choosing a form'>
                <CloseIcon />
              </CloseButton>
            </Header>

            <Toolbar>
              <SearchRow>
                <SearchOutlinedIcon style={{ fontSize: 20, color: '#8a8a94' }} />
                <SearchInput
                  data-tour-id='formPicker.search'
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder='Search forms by name'
                  aria-label='Search forms by name'
                />
              </SearchRow>
              <FilterRow>
                <FilterField>
                  <FilterLabel htmlFor='form-picker-category'>Type of form</FilterLabel>
                  <SearchableSelect
                    id='form-picker-category'
                    value={category || null}
                    onChange={value => setCategory(value || '')}
                    options={categories.map(option => ({ value: option.value, label: option.label }))}
                    clearable
                    clearLabel='All types'
                    placeholder='All types'
                    searchPlaceholder='Search types…'
                    ariaLabel='Type of form'
                  />
                </FilterField>
                <FilterField>
                  <FilterLabel htmlFor='form-picker-sort'>Order</FilterLabel>
                  <SearchableSelect
                    id='form-picker-sort'
                    value={sort}
                    onChange={value => setSort(value)}
                    options={SORT_OPTIONS.map(option => ({ value: option.value, label: option.label }))}
                    searchable={false}
                    ariaLabel='Order'
                  />
                </FilterField>
                <SystemToggle>
                  <SystemCheckbox
                    id='form-picker-system'
                    type='checkbox'
                    checked={showSystem}
                    onChange={event => setShowSystem(event.target.checked)}
                  />
                  <SystemToggleText htmlFor='form-picker-system'>
                    <SystemToggleLabel>Show system forms</SystemToggleLabel>
                    <SystemToggleHint>
                      System forms are ready-made by Building Passport. Untick to see only your own
                      and shared forms.
                    </SystemToggleHint>
                  </SystemToggleText>
                </SystemToggle>
              </FilterRow>
            </Toolbar>

            <Body data-tour-id='formPicker.list'>
              {error && (
                <ErrorBanner role='alert'>
                  <ErrorOutlineIcon style={{ fontSize: 18 }} />
                  <span>{error}</span>
                </ErrorBanner>
              )}

              {showRecommendations && (
                <RecommendSection aria-label='Recommended forms for this obligation'>
                  <RecommendHeading>Recommended for this obligation</RecommendHeading>
                  <RecommendHelp>
                    Suggested from the obligation's name and the forms already chosen for it at your other buildings. You can still pick any form below.
                  </RecommendHelp>
                  {recommendationsLoading ? (
                    <SkeletonStack>
                      {Array.from({ length: 2 }).map((_, index) => (
                        <SkeletonCard key={index}>
                          <SkeletonLine $w='50%' $h={16} />
                          <SkeletonLine $w='65%' $h={12} />
                        </SkeletonCard>
                      ))}
                    </SkeletonStack>
                  ) : (
                    recommendations.map(item => renderRow(item.template, item))
                  )}
                  <AllFormsHeading>All forms</AllFormsHeading>
                </RecommendSection>
              )}
              {allowNone && (
                <TemplateRow
                  $selected={!value}
                  onClick={() => handleUse(null)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') handleUse(null)
                  }}
                >
                  <RowMain>
                    <RowTitleLine>
                      <RowTitle>{noneLabel}</RowTitle>
                      {!value && <SelectedFlag><CheckCircleIcon style={{ fontSize: 16 }} /> Currently chosen</SelectedFlag>}
                    </RowTitleLine>
                    <RowDescription>{noneDescription}</RowDescription>
                  </RowMain>
                  <RowActions>
                    <UseButton type='button' onClick={event => { event.stopPropagation(); handleUse(null) }}>
                      Use no form
                    </UseButton>
                  </RowActions>
                </TemplateRow>
              )}

              {loading ? (
                <SkeletonStack>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonCard key={index}>
                      <SkeletonLine $w='45%' $h={16} />
                      <SkeletonLine $w='70%' $h={12} />
                      <SkeletonLine $w='30%' $h={12} />
                    </SkeletonCard>
                  ))}
                </SkeletonStack>
              ) : templates.length === 0 ? (
                <EmptyState>
                  <EmptyTitle>No forms found</EmptyTitle>
                  <EmptyText>
                    {debouncedSearch || category || !showSystem
                      ? 'Nothing matches your search. Try clearing the search box, choosing “All types”, or ticking “Show system forms”.'
                      : 'You do not have any forms yet. Create your first one below.'}
                  </EmptyText>
                  <CreateButton type='button' onClick={() => { setActiveTemplate(null); setMode('create') }}>
                    <AddIcon style={{ fontSize: 18 }} />
                    Create a new form
                  </CreateButton>
                </EmptyState>
              ) : (
                <>
                  {listTemplates.map(template => renderRow(template))}
                  <InfiniteScrollSentinel
                    hasMore={hasMore}
                    loading={loadingMore}
                    onLoadMore={() => fetchPage(meta.currentPage + 1)}
                    label='Loading more forms…'
                  />
                </>
              )}
            </Body>

            <Footer>
              <FooterCount>
                {templates.length > 0 && `Showing ${templates.length} of ${meta.total} form${meta.total === 1 ? '' : 's'}`}
              </FooterCount>
              <FooterActions>
                <GhostButton type='button' onClick={() => { setActiveTemplate(null); setMode('create') }}>
                  <AddIcon style={{ fontSize: 18 }} />
                  Create a new form
                </GhostButton>
                <CancelButton type='button' onClick={onClose}>Cancel</CancelButton>
              </FooterActions>
            </Footer>
          </>
        )}

        {mode === 'preview' && (
          <>
            <Header>
              <HeaderText>
                <BackButton type='button' onClick={backToList}>
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  Back to all forms
                </BackButton>
                <DialogTitle>{activeTemplate?.name}</DialogTitle>
                <DialogDescription>
                  This is a preview. Nothing you type here is saved.
                </DialogDescription>
              </HeaderText>
              <CloseButton type='button' onClick={onClose} aria-label='Close'>
                <CloseIcon />
              </CloseButton>
            </Header>
            <Body>
              {activeTemplateLoading ? (
                <SkeletonStack>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <SkeletonCard key={index}>
                      <SkeletonLine $w='40%' $h={16} />
                      <SkeletonLine $w='90%' $h={12} />
                    </SkeletonCard>
                  ))}
                </SkeletonStack>
              ) : (
                <FormRenderer
                  schema={activeTemplate?.schema || { sections: [] }}
                  responses={{}}
                  onAutoSave={() => {}}
                  readOnly
                  isOnline
                />
              )}
            </Body>
            <Footer>
              <FooterCount>Read-only preview. Press Esc to go back.</FooterCount>
              <FooterActions>
                {activeTemplate?.canEdit && (
                  <GhostButton type='button' onClick={() => setMode('edit')}>
                    <EditOutlinedIcon style={{ fontSize: 18 }} />
                    Edit this form
                  </GhostButton>
                )}
                <UseButton type='button' onClick={() => handleUse(activeTemplate)}>
                  Use this form
                </UseButton>
              </FooterActions>
            </Footer>
          </>
        )}

        {(mode === 'edit' || mode === 'create') && (
          <>
            <Header>
              <HeaderText>
                <BackButton type='button' onClick={backToList}>
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  Back to all forms
                </BackButton>
                <DialogTitle>
                  {mode === 'create' ? 'Create a new form' : `Edit: ${activeTemplate?.name || ''}`}
                </DialogTitle>
                <DialogDescription>
                  {mode === 'create'
                    ? 'Give your form a name, then add the questions people will answer.'
                    : 'Change the questions people answer, and choose who can see this form.'}
                </DialogDescription>
              </HeaderText>
              <CloseButton type='button' onClick={onClose} aria-label='Close'>
                <CloseIcon />
              </CloseButton>
            </Header>
            <Body>
              {error && (
                <ErrorBanner role='alert'>
                  <ErrorOutlineIcon style={{ fontSize: 18 }} />
                  <span>{error}</span>
                </ErrorBanner>
              )}
              {activeTemplateLoading ? (
                <SkeletonStack>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <SkeletonCard key={index}>
                      <SkeletonLine $w='40%' $h={16} />
                      <SkeletonLine $w='90%' $h={12} />
                    </SkeletonCard>
                  ))}
                </SkeletonStack>
              ) : (
                <>
                  <FormBuilder
                    template={mode === 'create' ? null : activeTemplate}
                    onSave={handleSaveTemplate}
                    saving={saving}
                    formTemplates={templates}
                    fileTypes={fileTypes}
                  />
                  {mode === 'edit' && activeTemplate?.id && (
                    <FormTemplateSharingPanel
                      template={activeTemplate}
                      onSaved={handleSharingSaved}
                    />
                  )}
                </>
              )}
            </Body>
            {mode === 'edit' && activeTemplate?.id && (
              <Footer>
                <FooterCount>Save your changes above before using this form.</FooterCount>
                <FooterActions>
                  <UseButton type='button' onClick={() => handleUse(activeTemplate)}>
                    Use this form
                  </UseButton>
                </FooterActions>
              </Footer>
            )}
          </>
        )}
      </Dialog>
    </Overlay>
  )

  return createPortal(body, document.body)
}

export default FormTemplatePickerModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100200;
  background: rgba(17, 17, 22, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  width: min(960px, 100%);
  max-height: min(88vh, 900px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(17, 17, 22, 0.16);
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #ececf0;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1c1c22;
`

const DialogDescription = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #5f5f6a;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  min-height: 40px;
  padding: 0 12px 0 8px;
  margin-left: -8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #33becc;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e6f7f9;
  }
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: 1px solid #e2e2e8;
  border-radius: 10px;
  background: #fff;
  color: #4a4a55;
  cursor: pointer;

  &:hover {
    background: #f1f1f4;
  }
`

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #ececf0;
  background: #fafafb;
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  min-height: 46px;
  background: #fff;
  border: 1px solid #dedee5;
  border-radius: 10px;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  color: #1c1c22;
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
`

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
`

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #5f5f6a;
`

const SystemToggle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 260px;
  padding: 8px 12px;
  border: 1px solid #e2e2e8;
  border-radius: 10px;
  background: #fff;
`

const SystemCheckbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #33becc;
`

const SystemToggleText = styled.label`
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
`

const SystemToggleLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const SystemToggleHint = styled.span`
  font-size: 13px;
  line-height: 1.45;
  color: #6b6b76;
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TemplateRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${({ $selected }) => ($selected ? '#33becc' : '#e6e6ea')};
  border-radius: 12px;
  background: ${({ $selected }) => ($selected ? '#e6f7f9' : '#fff')};
  cursor: pointer;

  &:hover {
    border-color: ${({ $selected }) => ($selected ? '#33becc' : '#c9c9d2')};
    background: ${({ $selected }) => ($selected ? '#e6f7f9' : '#fafafb')};
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
`

const RowTitleLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const RowTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1c1c22;
`

const RowBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

const SelectedFlag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
`

const RowDescription = styled.span`
  font-size: 15px;
  line-height: 1.5;
  color: #5f5f6a;
`

const RecommendReason = styled.span`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: #1f7f89;
`

const RecommendSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const RecommendHeading = styled.h3`
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 700;
  color: #12132e;
`

const RecommendHelp = styled.p`
  margin: 0 0 2px;
  font-size: 14px;
  line-height: 1.5;
  color: #5f5f6a;
`

const AllFormsHeading = styled.h3`
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 700;
  color: #12132e;
`

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 13px;
  color: #7a7a85;
`

const MetaItem = styled.span``

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
`

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  background: #fff;
  color: #3d3d47;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f1f4;
  }
`

const UseButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #2aa6b3;
  }
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  background: #fff;
  color: #3d3d47;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f1f4;
  }
`

const CreateButton = styled(UseButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border: 1px solid #eeeef1;
  border-radius: 12px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  text-align: center;
`

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1c1c22;
`

const EmptyText = styled.p`
  margin: 0 0 6px;
  max-width: 460px;
  font-size: 15px;
  line-height: 1.55;
  color: #5f5f6a;
`

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fdecea;
  color: #b00020;
  font-size: 14px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 24px;
  border-top: 1px solid #ececf0;
  background: #fafafb;
`

const FooterCount = styled.span`
  font-size: 14px;
  color: #62626d;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
